import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PrintReceiptDialog from '../components/PrintReceiptDialog';
import { useAutomaticPrintingPlacement } from '../hooks/useiOSPlacementId';
import usePrintReceipt from '../hooks/usePrintReceipt';
import useSoftpayReceipt from '../hooks/useSoftpayReceipt';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useSoftpayAppId } from './SoftpayAppIdStore';
import { usePrintReceiptOnS1F2, usePrintSoftpayReceiptOnF1 } from '@ordr-as/ordr-service-types/epos';
import { isF1Device } from '../utils/isF1Device';
import { isS1F2Device } from '../utils/isS1F2Device';
import { getPaymentMethodById } from './PaymentMethodStore';

export interface PrintReceiptContextType {
  bookingReferenceToPrint: string | undefined;
  setBookingReferenceToPrint: (reference: string) => void;
  handlePrintReceipt: (reference: string, paymentMethodId?: string) => void;
  isBatteryTooLow: boolean | null;
  showBatteryWarningDialog: boolean;
}

export const PrintReceiptContext = createContext<PrintReceiptContextType | undefined>(undefined);

const getBookingReferenceFromPaymentReference = (paymentReference: string) => {
  const split = paymentReference.split('-');
  return split.length > 2 ? paymentReference : split[0];
};

interface ProviderProps {
  children?: React.ReactNode;
}

interface PendingS1F2Print {
  paymentMethodId: string;
  paymentReference: string;
  label: string;
}

export const PrintReceiptContextProvider = ({ children }: ProviderProps) => {
  const [bookingReferenceToPrint, setBookingReferenceToPrint] = useState<string>();
  const [pendingS1F2Print, setPendingS1F2Print] = useState<PendingS1F2Print>();
  const [isBatteryTooLow, setIsBatteryTooLow] = useState<boolean | null>(null);
  const [showBatteryWarningDialog, setShowBatteryWarningDialog] = useState<boolean>(false);
  const { defaultPlacement } = useAutomaticPrintingPlacement();
  const { printReceipt } = usePrintReceipt();
  const printSoftpayReceipt = useSoftpayReceipt();
  const softpayAppId = useSoftpayAppId();
  const isF1 = isF1Device();
  const { mutate: printHubReceipt } = usePrintSoftpayReceiptOnF1();
  const isS1F2 = isS1F2Device();
  const { mutate: printS1F2Receipt, isPending: isPrintingS1F2 } = usePrintReceiptOnS1F2({
    mutation: {
      onSuccess: () => {
        setBookingReferenceToPrint(undefined);
        setPendingS1F2Print(undefined);
      },
    },
  });

  const handlePrintReceipt = useCallback(
    async (paymentReference: string, paymentMethodId?: string) => {
      let batteryTooLow = false;
      // @ts-ignore
      if (navigator.getBattery) {
        // @ts-ignore
        const battery = await navigator.getBattery();

        batteryTooLow = battery.level < 0.1;
        setIsBatteryTooLow(batteryTooLow);
      }

      if (batteryTooLow) {
        setShowBatteryWarningDialog(true);
        return;
      }

      if (isF1 && paymentMethodId) {
        printHubReceipt({
          data: {
            paymentMethodId,
            paymentReference,
            traceId: `${paymentReference}-${Math.floor(Date.now() / 1000)}`,
          },
        });
        return;
      }

      if (isS1F2 && paymentMethodId) {
        const label = getPaymentMethodById(paymentMethodId)?.label ?? paymentMethodId;
        const bookingReference = getBookingReferenceFromPaymentReference(paymentReference);
        setPendingS1F2Print({ paymentMethodId, paymentReference, label });
        setBookingReferenceToPrint(bookingReference);
        return;
      }

      if (softpayAppId) {
        printSoftpayReceipt(paymentReference);
        return;
      }

      const bookingReference = getBookingReferenceFromPaymentReference(paymentReference);

      if (defaultPlacement && bookingReference) {
        printReceipt(bookingReference, defaultPlacement);
        return;
      }

      setBookingReferenceToPrint(bookingReference);
    },
    [defaultPlacement, isF1, isS1F2, printHubReceipt, printReceipt, printSoftpayReceipt, softpayAppId]
  );

  const contextValue = useMemo(() => {
    const newValue = {
      bookingReferenceToPrint,
      handlePrintReceipt,
      isBatteryTooLow,
      setBookingReferenceToPrint,
      showBatteryWarningDialog,
    };

    return newValue;
  }, [bookingReferenceToPrint, handlePrintReceipt, isBatteryTooLow, showBatteryWarningDialog]);

  return (
    <PrintReceiptContext.Provider value={contextValue}>
      {showBatteryWarningDialog && (
        <Dialog
          title="Could not print receipt"
          onClose={() => setShowBatteryWarningDialog(false)}
          actionLabel="OK"
          onAction={() => setShowBatteryWarningDialog(false)}
        >
          <div>
            <p>The device must have at least 10% battery to print. Charge the device and try again.</p>
          </div>
        </Dialog>
      )}
      {bookingReferenceToPrint && (
        <PrintReceiptDialog
          onCloseDialog={() => {
            setBookingReferenceToPrint(undefined);
            setPendingS1F2Print(undefined);
          }}
          reference={bookingReferenceToPrint}
          terminalPrint={
            pendingS1F2Print
              ? {
                  name: pendingS1F2Print.label,
                  isPending: isPrintingS1F2,
                  onPrint: () =>
                    printS1F2Receipt({
                      data: {
                        paymentMethodId: pendingS1F2Print.paymentMethodId,
                        paymentReference: pendingS1F2Print.paymentReference,
                        traceId: `${pendingS1F2Print.paymentReference}-${Math.floor(Date.now() / 1000)}`,
                      },
                    }),
                }
              : undefined
          }
        />
      )}
      {children}
    </PrintReceiptContext.Provider>
  );
};

export const usePrintReceiptContext = (): PrintReceiptContextType => {
  const context = useContext(PrintReceiptContext);
  if (!context) {
    throw new Error('usePrintReceiptContext must be used within a PrintReceiptProvider');
  }
  return context;
};
